import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { CheckCircle2, Headphones, Loader2, Info, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth";

interface SampleData {
  id: string;
  dimension: string;
  level: string;
  polarity: string;
  shortDescription: string;
  longDescription: string;
  levelDescription: string;
  audioUrl: string;
}

type DoneReason = "quota_reached" | "blocked" | "no_samples";

const DONE_MESSAGES: Record<DoneReason, { title: string; text: string }> = {
  quota_reached: {
    title: "Target Reached!",
    text: "You have reached your annotation target. Thank you for your contribution!",
  },
  blocked: {
    title: "No Tasks",
    text: "There are currently no annotation tasks for your account. Please contact your coordinator.",
  },
  no_samples: {
    title: "All Done!",
    text: "You have annotated all samples in your assigned pool. Thank you for your contribution!",
  },
};

interface PrefetchedSample {
  sample: SampleData;
  remaining: number;
  audioDurationMs: number;
}

export function AnnotationInterface() {
  const { token } = useAuth();
  const [sample, setSample] = useState<SampleData | null>(null);
  const [done, setDone] = useState<DoneReason | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagReason, setFlagReason] = useState("");
  const [flagSubmitting, setFlagSubmitting] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const timerStartRef = useRef<number>(0);
  const audioDurationMsRef = useRef<number>(0);
  const audioBlobUrlRef = useRef<string | null>(null);
  const prefetchedRef = useRef<PrefetchedSample | null>(null);
  const prefetchingRef = useRef(false);

  /** Fetch a sample's metadata + audio blob, returning a ready-to-use PrefetchedSample */
  const fetchSample = useCallback(async (): Promise<{ done: true; reason: DoneReason } | { done: false; data: PrefetchedSample }> => {
    const res = await fetch("/api/samples/next", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("Failed to load sample");
    const json = await res.json();
    if (json.done) return { done: true, reason: json.reason ?? "no_samples" };

    const audioRes = await fetch(json.sample.audioUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!audioRes.ok) throw new Error("Failed to load audio");
    const blob = await audioRes.blob();
    const blobUrl = URL.createObjectURL(blob);

    // Decode duration from the blob
    let audioDurationMs = 0;
    try {
      const tmpAudio = new Audio(blobUrl);
      await new Promise<void>((resolve) => {
        tmpAudio.addEventListener("loadedmetadata", () => resolve(), { once: true });
        tmpAudio.addEventListener("error", () => resolve(), { once: true });
        tmpAudio.load();
      });
      if (isFinite(tmpAudio.duration)) {
        audioDurationMs = Math.round(tmpAudio.duration * 1000);
      }
    } catch { /* ignore */ }

    return {
      done: false,
      data: {
        sample: {
          ...json.sample,
          audioUrl: blobUrl,
        },
        remaining: json.remaining,
        audioDurationMs,
      },
    };
  }, [token]);

  /** Start prefetching the next sample in the background */
  const prefetchNext = useCallback(async () => {
    if (prefetchingRef.current) return;
    prefetchingRef.current = true;
    try {
      const result = await fetchSample();
      if (!result.done) {
        prefetchedRef.current = result.data;
      }
    } catch { /* prefetch failures are silent */ }
    prefetchingRef.current = false;
  }, [fetchSample]);

  /** Apply a prefetched sample or fetch fresh */
  const applyOrFetch = useCallback(async () => {
    setLoading(true);
    setAudioReady(false);
    // Revoke previous blob URL to free memory
    if (audioBlobUrlRef.current) {
      URL.revokeObjectURL(audioBlobUrlRef.current);
      audioBlobUrlRef.current = null;
    }

    try {
      // Use prefetched sample if available
      const pre = prefetchedRef.current;
      if (pre) {
        prefetchedRef.current = null;
        audioBlobUrlRef.current = pre.sample.audioUrl;
        audioDurationMsRef.current = pre.audioDurationMs;
        setSample(pre.sample);
        setRemaining(pre.remaining);
        setLoading(false);
        // Immediately start prefetching the one after
        prefetchNext();
        return;
      }

      // No prefetch available — fetch now
      const result = await fetchSample();
      if (result.done) {
        setDone(result.reason);
        setSample(null);
      } else {
        audioBlobUrlRef.current = result.data.sample.audioUrl;
        audioDurationMsRef.current = result.data.audioDurationMs;
        setSample(result.data.sample);
        setRemaining(result.data.remaining);
        // Start prefetching the next one
        prefetchNext();
      }
    } catch {
      toast.error("Failed to load next sample");
    } finally {
      setLoading(false);
    }
  }, [fetchSample, prefetchNext]);

  useEffect(() => {
    applyOrFetch();
    return () => {
      if (audioBlobUrlRef.current) {
        URL.revokeObjectURL(audioBlobUrlRef.current);
      }
      // Also revoke any prefetched blob
      if (prefetchedRef.current) {
        URL.revokeObjectURL(prefetchedRef.current.sample.audioUrl);
        prefetchedRef.current = null;
      }
    };
  }, [applyOrFetch]);

  const handleCanPlayThrough = () => {
    setAudioReady(true);
    timerStartRef.current = performance.now();
    // Use pre-decoded duration if available, otherwise read from element
    if (audioDurationMsRef.current === 0 && audioRef.current && isFinite(audioRef.current.duration)) {
      audioDurationMsRef.current = Math.round(audioRef.current.duration * 1000);
    }
  };

  const handleFlag = async () => {
    if (!sample || !flagReason.trim()) return;
    setFlagSubmitting(true);
    try {
      const res = await fetch("/api/flags", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ sampleId: sample.id, reason: flagReason.trim() }),
      });
      if (res.ok) {
        toast.success("Sample flagged — thank you!");
        setFlagOpen(false);
        setFlagReason("");
      } else {
        const data = await res.json();
        toast.error(data.error || "Failed to flag sample");
      }
    } catch {
      toast.error("Failed to flag sample");
    } finally {
      setFlagSubmitting(false);
    }
  };

  const handleRate = async (rating: string) => {
    if (!sample || submitting) return;

    const reactionTimeMs = Math.round(performance.now() - timerStartRef.current);
    const sampleTimeMs = audioDurationMsRef.current;
    setSubmitting(true);

    try {
      const res = await fetch("/api/annotations", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sampleId: sample.id,
          rating,
          reactionTimeMs,
          sampleTimeMs,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        if (res.status === 409 || res.status === 403) {
          // Already annotated (409) or target reached (403): move on, /next reports done
          // Invalidate prefetch since the state has changed
          if (prefetchedRef.current) {
            URL.revokeObjectURL(prefetchedRef.current.sample.audioUrl);
            prefetchedRef.current = null;
          }
          applyOrFetch();
          return;
        }
        toast.error(data.error || "Failed to save annotation");
        return;
      }

      // Invalidate prefetch since the remaining count has changed
      if (prefetchedRef.current) {
        URL.revokeObjectURL(prefetchedRef.current.sample.audioUrl);
        prefetchedRef.current = null;
      }

      // Auto-advance
      applyOrFetch();
    } catch {
      toast.error("Failed to save annotation");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Card className="max-w-lg mx-auto text-center">
        <CardContent className="pt-8 pb-8 space-y-4">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
          <h2 className="text-2xl font-bold">{DONE_MESSAGES[done].title}</h2>
          <p className="text-muted-foreground">
            {DONE_MESSAGES[done].text}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading && !sample) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Headphones className="h-5 w-5" />
          Annotation
        </h2>
        <Badge variant="outline">{remaining} remaining</Badge>
      </div>

      <Card className="min-h-[340px]">
        {sample ? (
          <>
            <CardHeader className="pb-3">
              <CardTitle className="text-center space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <span className="relative group">
                    <button
                      type="button"
                      onClick={() => { setFlagOpen(true); setFlagReason(""); }}
                      className="text-xl leading-none hover:scale-125 transition-transform cursor-pointer"
                    >
                      ❗
                    </button>
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 text-xs text-center bg-popover text-popover-foreground border rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      Flag this sample as problematic (e.g., corrupted, very weird)
                    </span>
                  </span>
                  <Badge className="text-lg px-4 py-1">
                    {sample.shortDescription}
                  </Badge>
                  {sample.longDescription && (
                    <span className="relative group">
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 text-xs bg-popover text-popover-foreground border rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                        {sample.longDescription}
                      </span>
                    </span>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Level description */}
              {sample.levelDescription && (
                <div className="text-center p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium">{sample.levelDescription}</p>
                </div>
              )}

              {/* Audio Player */}
              <div className="flex justify-center">
                <audio
                  ref={audioRef}
                  src={sample.audioUrl}
                  controls
                  autoPlay
                  onCanPlayThrough={handleCanPlayThrough}
                  className="w-full"
                  key={sample.id}
                />
              </div>

              {!audioReady && (
                <p className="text-center text-sm text-muted-foreground">Loading audio...</p>
              )}

              {/* Rating question */}
              <div className="text-center text-sm text-muted-foreground mb-2">
                The description fits to the audio:
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  size="lg"
                  className="h-16 text-lg font-medium border-green-200 hover:bg-green-50 hover:border-green-400 dark:border-green-900 dark:hover:bg-green-950"
                  onClick={() => handleRate("yes")}
                  disabled={!audioReady || submitting}
                >
                  Yes
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="h-16 text-lg font-medium border-red-200 hover:bg-red-50 hover:border-red-400 dark:border-red-900 dark:hover:bg-red-950"
                  onClick={() => handleRate("no")}
                  disabled={!audioReady || submitting}
                >
                  No
                </Button>
              </div>

              {submitting && (
                <div className="flex justify-center">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              )}
            </CardContent>
          </>
        ) : (
          <CardContent className="flex items-center justify-center h-full min-h-[300px]">
            <Loader2 className="h-8 w-8 animate-spin" />
          </CardContent>
        )}
      </Card>

      {/* Flag overlay */}
      {flagOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setFlagOpen(false)}>
          <div className="bg-card border rounded-lg shadow-lg w-full max-w-md mx-4 p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                Flag Sample
              </h3>
              <button type="button" onClick={() => setFlagOpen(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground">
              Only flag if this sample is <strong>really</strong> problematic (e.g., very out of distribution, corrupted, should be removed).
            </p>
            <textarea
              className="w-full h-24 p-3 text-sm border rounded-md bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Describe the issue (e.g., weird noise, wrong language, corrupted audio...)"
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              maxLength={500}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setFlagOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleFlag}
                disabled={!flagReason.trim() || flagSubmitting}
              >
                {flagSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Submit Flag
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
